import { Component } from '@theme/component';
import { ThemeEvents, CartAddEvent, CartErrorEvent } from '@theme/events';

/**
 * `<product-form-component>` — controla el envío del formulario "Agregar al carrito".
 *
 * PRINCIPIO DE DISEÑO (esto es lo que corrige el bug de desfase):
 * Nunca leemos el DOM "a ciegas" ni adivinamos con un setTimeout fijo. El único ID de
 * variante que este componente envía es el que vive en el input oculto `ref="variantId"`.
 * Ese input es la fuente de verdad y SOLO se actualiza cuando `variant-picker.js` confirma
 * -de forma asíncrona y contra el servidor- cuál es la variante real (evento `variant:update`).
 *
 * Si el cliente hace clic en "Agregar al carrito" mientras esa confirmación todavía está en
 * vuelo (por ejemplo, cambió de talla y dio clic de inmediato), el submit ESPERA la
 * confirmación real en lugar de enviar el valor viejo o adivinar con un retraso arbitrario.
 * Así se elimina el "un paso atrás" de forma determinista, sin importar la latencia de red.
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId
 * @property {HTMLElement} productFormButtons
 * @property {HTMLElement} [liveRegion]
 * @property {HTMLElement} [addToCartTextError]
 * @extends {Component<ProductFormRefs>}
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'productFormButtons'];

  /** @type {boolean} True mientras hay una confirmación de variante en vuelo. */
  #variantChangeInProgress = false;

  /** @type {AbortController | null} */
  #addToCartAbortController = null;

  connectedCallback() {
    super.connectedCallback();

    // IMPORTANTE: <variant-picker> y <product-form-component> son bloques HERMANOS
    // (ver blocks/_product-details.liquid), no uno dentro del otro. Los eventos
    // personalizados solo burbujean por los ancestros reales del elemento que los dispara,
    // así que hay que escucharlos en `document` -mismo patrón que usa cart-icon.js- para
    // que este componente se entere de verdad cuando el usuario cambia de talla/color.

    // 'variant:selected' se dispara de forma SÍNCRONA en el mismo clic del swatch/talla:
    // en ese instante bloqueamos el botón para que un doble clic rápido no pueda enviar
    // la variante que está a punto de quedar obsoleta.
    document.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);

    // 'variant:update' se dispara cuando variant-picker.js YA confirmó, contra el servidor,
    // cuál es la variante real. Este es el único lugar donde el input oculto "id" se actualiza.
    document.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
    document.removeEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.#addToCartAbortController?.abort();
  }

  /**
   * Como ahora escuchamos en `document` (variant-picker y product-form-component son
   * hermanos, no está uno dentro del otro), hay que confirmar que el evento pertenece a
   * ESTE producto y no a otro (ej. otra tarjeta de producto con "compra rápida" en la
   * misma página). Ambos comparten el mismo data-section-id dentro de una misma ficha.
   * @param {Event} event
   */
  #belongsToThisProduct(event) {
    const source = event.target;
    if (!(source instanceof Element)) return true;

    const sourceSectionId = source.closest('[data-section-id]')?.getAttribute('data-section-id');
    if (!sourceSectionId || !this.dataset.sectionId) return true;

    return sourceSectionId === this.dataset.sectionId;
  }

  /**
   * @param {Event} event
   */
  #handleVariantSelected = (event) => {
    if (!this.#belongsToThisProduct(event)) return;

    this.#variantChangeInProgress = true;
    this.#setButtonBusy(true);
  };

  /**
   * @param {CustomEvent} event
   */
  #handleVariantUpdate = (event) => {
    if (!this.#belongsToThisProduct(event)) return;

    // Ignora actualizaciones que pertenezcan a otro producto/sección (listados combinados,
    // quick add en otra tarjeta, etc.) para no pisar el ID de este formulario.
    const eventProductId = event.detail?.data?.productId;
    if (eventProductId && this.dataset.productId && eventProductId !== this.dataset.productId) return;

    const variant = event.detail?.resource;

    if (variant?.id && this.refs.variantId) {
      this.refs.variantId.value = String(variant.id);
    } else if (this.refs.variantId) {
      // No hay variante disponible para la combinación seleccionada (ej. combinación sin stock
      // configurado): vaciamos el id para impedir que se envíe una variante incorrecta.
      this.refs.variantId.value = '';
    }

    this.#variantChangeInProgress = false;
    this.#setButtonBusy(false, variant);
  };

  /**
   * @param {boolean} busy
   * @param {{available?: boolean}} [variant]
   */
  #setButtonBusy(busy, variant) {
    const button = this.#submitButton;
    if (!button) return;

    if (busy) {
      button.setAttribute('aria-disabled', 'true');
      button.classList.add('loading');
      return;
    }

    button.classList.remove('loading');

    if (variant && variant.available === false) {
      button.setAttribute('aria-disabled', 'true');
    } else {
      button.removeAttribute('aria-disabled');
    }
  }

  get #submitButton() {
    return /** @type {HTMLButtonElement | null} */ (
      this.refs.productFormButtons?.querySelector('button[type="submit"], button[name="add"]')
    );
  }

  /**
   * Vinculado de forma declarativa vía `on:submit="/handleSubmit"` en blocks/buy-buttons.liquid.
   * @param {SubmitEvent} event
   */
  async handleSubmit(event) {
    event.preventDefault();

    const form = /** @type {HTMLFormElement | null} */ (event.target instanceof HTMLFormElement ? event.target : this.querySelector('form'));
    if (!form) return;

    const submitButton = this.#submitButton;
    if (submitButton?.getAttribute('aria-disabled') === 'true' && !this.#variantChangeInProgress) return;

    this.#hideError();

    // *** Aquí está la corrección real del bug ***
    // Si el usuario cambió la variante justo antes de dar clic, esperamos la confirmación
    // real del servidor (evento variant:update) en vez de leer un valor potencialmente viejo.
    if (this.#variantChangeInProgress) {
      const confirmed = await this.#waitForVariantConfirmation();
      if (!confirmed) {
        this.#showError();
        this.#setButtonBusy(false);
        return;
      }
    }

    const variantIdInput = this.refs.variantId;
    const variantId = variantIdInput?.value;

    if (!variantId) {
      this.#showError();
      return;
    }

    submitButton?.setAttribute('aria-disabled', 'true');
    submitButton?.classList.add('loading');

    this.#addToCartAbortController?.abort();
    this.#addToCartAbortController = new AbortController();

    const formData = new FormData(form);
    // Aseguramos explícitamente que el id enviado sea el confirmado, incluso si algún otro
    // campo del form quedó desincronizado por cualquier razón ajena a este componente.
    formData.set('id', variantId);

    const sectionsToRender = this.#getCartSectionsToRender();
    if (sectionsToRender.length) {
      formData.append('sections', sectionsToRender.join(','));
      formData.append('sections_url', window.location.pathname);
    }

    try {
      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        body: formData,
        signal: this.#addToCartAbortController.signal,
      });

      const result = await response.json();

      if (result.status) {
        this.#showError(result.description || result.message);
        document.dispatchEvent(
          new CartErrorEvent(this.dataset.sectionId ?? '', { message: result.description, source: 'product-form-component' })
        );
        return;
      }

      if (this.refs.liveRegion) {
        this.refs.liveRegion.textContent = window.Theme?.translations?.added || '';
      }

      document.dispatchEvent(
        new CartAddEvent(result, this.dataset.sectionId ?? '', {
          didError: false,
          source: 'product-form-component',
          productId: this.dataset.productId,
          variantId,
          itemCount: result.quantity ?? result.item_count,
          sections: result.sections,
        })
      );
    } catch (error) {
      if (error?.name !== 'AbortError') {
        console.error(error);
        this.#showError();
      }
    } finally {
      submitButton?.removeAttribute('aria-disabled');
      submitButton?.classList.remove('loading');
    }
  }

  /**
   * Espera a que llegue la confirmación real de variant-picker.js. Sirve de red de seguridad
   * ante fallos de red (no forma parte de la lógica "feliz"): si la petición de variant-picker
   * fallara o tardara demasiado, no dejamos el botón bloqueado para siempre.
   * @returns {Promise<boolean>}
   */
  #waitForVariantConfirmation() {
    if (!this.#variantChangeInProgress) return Promise.resolve(true);

    return new Promise((resolve) => {
      /** @param {Event} e */
      const onUpdate = (e) => {
        cleanup();
        resolve(true);
      };

      const timeout = setTimeout(() => {
        cleanup();
        resolve(false);
      }, 6000);

      const cleanup = () => {
        this.removeEventListener(ThemeEvents.variantUpdate, onUpdate);
        clearTimeout(timeout);
      };

      this.addEventListener(ThemeEvents.variantUpdate, onUpdate, { once: true });
    });
  }

  /**
   * Recolecta los IDs de sección que deben re-renderizarse tras agregar al carrito
   * (cart drawer, etc.), sin depender de un método inexistente en cart-drawer.
   * @returns {string[]}
   */
  #getCartSectionsToRender() {
    const ids = new Set();

    const cartDrawer = document.querySelector('cart-drawer-component[data-section-id]');
    if (cartDrawer instanceof HTMLElement && cartDrawer.dataset.sectionId) {
      ids.add(cartDrawer.dataset.sectionId);
    }

    // Permite que otros componentes se inscriban explícitamente si necesitan re-renderizarse
    // tras un add-to-cart, sin acoplar este archivo a la estructura exacta del header.
    document.querySelectorAll('[data-cart-render-section-id]').forEach((el) => {
      const id = /** @type {HTMLElement} */ (el).dataset.cartRenderSectionId;
      if (id) ids.add(id);
    });

    return Array.from(ids);
  }

  /**
   * @param {string} [errorMessage]
   */
  #showError(errorMessage) {
    const wrapper = this.refs.addToCartTextError;
    if (!wrapper) return;
    wrapper.classList.remove('hidden');
    wrapper.setAttribute('aria-hidden', 'false');
    if (errorMessage) wrapper.setAttribute('title', errorMessage);
  }

  #hideError() {
    const wrapper = this.refs.addToCartTextError;
    if (!wrapper) return;
    wrapper.classList.add('hidden');
    wrapper.setAttribute('aria-hidden', 'true');
  }
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}